<template>
<div class="px-1 pt-1">
  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteLabelTemplateModal" ref="deleteLabelTemplateModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="labelTemplateToDelete">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('page.labelTemplate.deleteConfirmTitle') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('page.labelTemplate.deleteConfirmMessage', { code: labelTemplateToDelete.code }) }}</p>
          <div class="card">
            <div class="card-body">
              <h6 class="card-title">{{ labelTemplateToDelete.code }}</h6>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="confirmDeleteLabelTemplate" :disabled="isDeleting">
            <div v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></div>
            <font-awesome-icon v-else :icon="['fas', 'trash']" class="me-2" />
            {{ $t('action.delete') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="isDeleting">
            <font-awesome-icon :icon="['fas', 'xmark']" class="me-2" />
            {{ $t('action.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="input-group pb-3">
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
    <RouterLink :to="{ name: 'label-template-create'}" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'plus']" />
    </RouterLink>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="labelTemplates" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="labelTemplates.length > 0" class="table table-hover table-sm table-responsive-lg table-fit align-middle">
      <thead>
      <tr>
        <th></th>
        <th class="mel-min-w-200">{{ $t('field.generic.code') }}</th>
        <th></th>
      </tr>
      </thead>
      <tbody id="scrollable-content">
      <tr v-for="labelTemplate in labelTemplates" :key="labelTemplate.id">
        <td>
          <RouterLink :to="{ name: 'label-template', params: { templateId: labelTemplate.id }}" class="btn btn-outline-secondary btn-sm">
            <font-awesome-icon :icon="['fas', 'pen']" />
          </RouterLink>
        </td>
        <td>{{labelTemplate.code}}</td>
        <td>
          <button class="btn btn-outline-danger" type="button" @click="showDeleteConfirmation(labelTemplate)">
            <font-awesome-icon :icon="['fas', 'trash']" />
          </button>
        </td>
      </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {Modal} from "bootstrap";
import {useLabelTemplatesStore} from '@/stores';
import {toast, type ToastOptions} from "vue3-toastify";
import {useI18n} from "vue-i18n";
import LabelTemplate from "@/models/frontend/LabelTemplate";

const i18n = useI18n();

const labelTemplateStore = useLabelTemplatesStore();
const labelTemplates = ref<LabelTemplate[]>([]);
const isLoading = ref(false);
const error = ref("");
const offsetTop = ref(0);

const deleteLabelTemplateModal = ref<HTMLElement | null>(null);
const labelTemplateToDelete = ref<LabelTemplate | null>(null);
const isDeleting = ref(false);

onMounted(async () => {
  await refresh();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const showDeleteConfirmation = (labelTemplate: LabelTemplate) => {
  labelTemplateToDelete.value = labelTemplate;
  if (deleteLabelTemplateModal.value) {
    const modal = new Modal(deleteLabelTemplateModal.value);
    modal.show();
  }
};

const hideDeleteModal = () => {
  if (deleteLabelTemplateModal.value) {
    const modal = Modal.getInstance(deleteLabelTemplateModal.value);
    modal?.hide();
  }
  labelTemplateToDelete.value = null;
};

const confirmDeleteLabelTemplate = async () => {
  if (!labelTemplateToDelete.value) return;

  const code = labelTemplateToDelete.value.code;
  try {
    isDeleting.value = true;
    await labelTemplateStore.delete(labelTemplateToDelete.value.id);

    if (labelTemplateStore.error) {
      toast.error(i18n.t('page.labelTemplate.deleteError', { error: labelTemplateStore.error }), { theme: 'colored', autoClose: 2000 } as ToastOptions);
    } else {
      toast.success(i18n.t('page.labelTemplate.deletedSuccessfully', { code }), { autoClose: 3000 });
    }

    hideDeleteModal();
    await refresh();
  } finally {
    isDeleting.value = false;
  }
};

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    labelTemplates.value = labelTemplateStore.getAll();
  }
  finally {
    error.value = labelTemplateStore.error;
    isLoading.value = false;
  }
}

</script>

<style scoped>
.mel-min-w-200 {
  min-width: 200px;
}
</style>
